import React from 'react';
import { useLanguage } from 'modules/LanguageContext';
import DICTIONARY from 'modules/i18n/dictionary';

// Stage 3.9 (Enterprise Candidate Experience Final UI Polish & UX Optimization): the single reusable
// primitive for centralized-language-resource text. Reads modules/LanguageContext.js's current
// locale/displayMode and renders accordingly:
//   - displayMode 'en'       -> English only
//   - displayMode 'regional' -> the selected regional locale only (falls back to English if the entry
//                                 has no translation for the current locale)
//   - displayMode 'bilingual' (default) -> "English / Regional", matching the single-line format
//     Stage 3.9 (Task 2) standardized on, so migrated strings need no extra CSS scoping.
//
// Phase C.3 (§10, "Missing Translation Fallback"): a locale with no entry in the dictionary (every entry
// today only has `en`/`mr` — see modules/i18n/dictionary.js's own header comment) now falls back
// straight to English, never to Marathi. Previously this fell back to `entry.mr` for ANY locale lacking
// its own key — meaning selecting Kannada/Hindi/etc. as the Regional Language silently rendered Marathi
// text mislabeled as the selected language, an "unexplained mixed-language fallback" the governing
// prompt explicitly forbids. Locale === 'mr' is unaffected by this fix: `entry['mr']` already resolves
// directly via `entry[locale]` when `locale === 'mr'`, so Marathi rendering is unchanged.
//
// `k` is a dot-path into modules/i18n/dictionary.js (e.g. "accessibilityToolbar.fontSize"). A missing
// key renders the key itself (loud-but-non-crashing fallback, same philosophy as modules/i18n.js's
// existing t() function) rather than silently rendering blank.
function resolveEntry(key) {
  const parts = String(key).split('.');
  let node = DICTIONARY;
  for (const part of parts) {
    if (!node || typeof node !== 'object') return null;
    node = node[part];
  }
  return (node && typeof node === 'object') ? node : null;
}

// Plain-string variant of the same lookup/render logic — for contexts where a JSX element can't be
// nested (e.g. <option> children, aria-label attributes). Kept in this file so there is exactly one
// implementation of the display-mode logic, not two that could drift apart.
// Sprint 2.6.7C-4 (Final Visual Polish, §6): only treat `entry[locale]` as the regional half when
// locale is an actual *non-English* selection (avoids `entry['en']` trivially equaling `en`).
// Phase C.3 (§10): if that locale has no entry, fall back to English — never to Marathi (see this
// file's header comment for why the old `|| entry.mr` fallback was a real defect, not a feature).
export function resolveText(key, locale, displayMode) {
  const entry = resolveEntry(key);
  if (!entry) return key;
  const en = entry.en || key;
  const regional = (locale && locale !== 'en' && entry[locale]) || en;
  if (displayMode === 'en') return en;
  if (displayMode === 'regional') return regional;
  return regional !== en ? (en + ' / ' + regional) : en;
}

export default function Text({ k, tag, className, ...rest }) {
  const { locale, displayMode } = useLanguage();
  const entry = resolveEntry(k);
  const Tag = tag || 'span';

  if (!entry) {
    return <Tag className={className} {...rest}>{k}</Tag>;
  }

  const en = entry.en || k;
  // See the identical fix/rationale on resolveText() above — same bug, same fix, kept in sync.
  const regional = (locale && locale !== 'en' && entry[locale]) || en;

  if (displayMode === 'en') {
    return <Tag className={className} {...rest}>{en}</Tag>;
  }
  if (displayMode === 'regional') {
    return <Tag className={className} {...rest}>{regional}</Tag>;
  }
  // bilingual (default)
  return <Tag className={className} {...rest}>{en}{regional !== en ? <> / {regional}</> : null}</Tag>;
}
